// PROMPT 147: elegir una plantilla aprobada de la marca, completar sus variables y mandarla.
// Sirve para escribirle a un cliente pasadas las 24 horas, para un chat nuevo y (PROMPT 151) para
// una difusion. `onEnviar(pedido)` hace el envio; si falla, tira el error y aca se muestra.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  Search,
  Send,
  Video,
} from "lucide-react";
import { categoriaNombre, idiomaNombre, minutosDesde, NOTA_COSTO } from "@/lib/plantillasWa";
import {
  encabezadoDeArchivo,
  valoresDeCampos,
  valoresIniciales,
  vistaDePlantilla,
} from "@/lib/enviarPlantillaWa";
import { BloquePlantilla } from "@/components/agente/BurbujaPlantilla";

const ROTULO_ARCHIVO = { IMAGE: "imagen", VIDEO: "video", DOCUMENT: "documento" };
const ACEPTA_ARCHIVO = { IMAGE: "image/jpeg,image/png", VIDEO: "video/mp4", DOCUMENT: "application/pdf" };
const MINUTOS_CACHE = 10; // si la copia de Meta tiene mas de 10 minutos, se vuelve a leer

function IconoArchivo({ formato }) {
  if (formato === "VIDEO") return <Video className="h-5 w-5" />;
  if (formato === "DOCUMENT") return <FileText className="h-5 w-5" />;
  return <ImageIcon className="h-5 w-5" />;
}

export default function ElegirPlantilla({ marcaSlug, idioma, sugeridos, onEnviar, textoBoton }) {
  const [lista, setLista] = useState(null); // null: leyendo
  const [wabaId, setWabaId] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [errorLista, setErrorLista] = useState("");
  const [q, setQ] = useState("");
  const [elegida, setElegida] = useState(null);
  const [vals, setVals] = useState({});
  const [archivo, setArchivo] = useState(null); // el que se subio con "Cambiar archivo"
  const [subiendo, setSubiendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState("");
  const pedirArchivo = useRef(null);

  const cargar = useCallback(async () => {
    const { data } = await api.get("/whatsapp/plantillas", {
      params: { marca_slug: marcaSlug, estado: "APPROVED" },
    });
    setLista(data?.plantillas || []);
    setWabaId(data?.waba_id || null);
    return data;
  }, [marcaSlug]);

  // Las aprobadas de la cuenta de la marca; si la copia es vieja, se actualiza desde Meta (solo lectura).
  useEffect(() => {
    let vigente = true;
    setLista(null);
    setElegida(null);
    setErrorLista("");
    (async () => {
      if (!marcaSlug) {
        setLista([]);
        return;
      }
      try {
        const data = await cargar();
        if (!vigente || !data?.waba_id) return;
        if (minutosDesde(data.sincronizada_en) > MINUTOS_CACHE) {
          setSincronizando(true);
          try {
            await api.post("/whatsapp/plantillas/sincronizar", { marca_slug: marcaSlug });
            if (vigente) await cargar();
          } catch {
            /* sigue con la copia guardada */
          } finally {
            if (vigente) setSincronizando(false);
          }
        }
      } catch (e) {
        if (vigente) {
          setErrorLista(formatApiError(e.response?.data?.detail) || "No se pudieron leer las plantillas.");
          setLista([]);
        }
      }
    })();
    return () => {
      vigente = false;
    };
  }, [marcaSlug, cargar]);

  const prefijo = String(idioma || "").slice(0, 2).toLowerCase();
  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    const delIdioma = (p) => (prefijo && String(p.idioma || "").toLowerCase().startsWith(prefijo) ? 0 : 1);
    return (lista || [])
      .filter((p) => String(p.categoria || "").toUpperCase() !== "AUTHENTICATION")
      .filter((p) => !t || `${p.nombre} ${p.texto_cuerpo || ""}`.toLowerCase().includes(t))
      .sort((a, b) => delIdioma(a) - delIdioma(b) || String(a.nombre).localeCompare(String(b.nombre)));
  }, [lista, q, prefijo]);

  const elegir = (p) => {
    setElegida(p);
    setVals(valoresIniciales(p.campos, sugeridos));
    setArchivo(null);
    setErrorEnvio("");
  };

  const formatoArchivo = elegida ? encabezadoDeArchivo(elegida) : null;
  const valores = elegida ? valoresDeCampos(elegida, elegida.campos, vals) : null;
  const vista = elegida ? vistaDePlantilla(elegida, valores, { conMarcas: true }) : null;
  const faltan = elegida ? vistaDePlantilla(elegida, valores).faltan : [];
  const sinArchivo = !!formatoArchivo && !archivo && !elegida?.encabezado_archivo_id;
  const puede = !!elegida && !faltan.length && !sinArchivo && !subiendo && !enviando;

  const subir = async (ev) => {
    const f = ev.target.files && ev.target.files[0];
    ev.target.value = "";
    if (!f || !formatoArchivo) return;
    setSubiendo(true);
    setErrorEnvio("");
    try {
      const fd = new FormData();
      fd.append("archivo", f);
      fd.append("tipo", formatoArchivo);
      const { data } = await api.post("/whatsapp/plantillas/archivos", fd);
      setArchivo({ archivo_id: data.archivo_id, nombre: data.nombre || f.name });
    } catch (e) {
      setErrorEnvio(formatApiError(e.response?.data?.detail) || "No se pudo cargar el archivo.");
    } finally {
      setSubiendo(false);
    }
  };

  const enviar = async () => {
    if (!puede) return;
    setEnviando(true);
    setErrorEnvio("");
    try {
      await onEnviar({
        plantilla_id: elegida.id,
        valores,
        ...(archivo ? { encabezado_archivo_id: archivo.archivo_id } : {}),
      });
    } catch (e) {
      setErrorEnvio(formatApiError(e.response?.data?.detail) || e.message || "No se pudo mandar la plantilla.");
    } finally {
      setEnviando(false);
    }
  };

  const sinPlantillas = lista !== null && !errorLista && !visibles.length && !q.trim();

  if (lista === null) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-slate-500" data-testid="wa-plantilla-leyendo">
        <Loader2 className="h-4 w-4 animate-spin" /> Leyendo las plantillas de la marca…
      </div>
    );
  }

  if (!elegida) {
    return (
      <div className="space-y-3" data-testid="wa-elegir-plantilla-lista">
        {sincronizando && (
          <p className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="wa-plantilla-sincronizando">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Actualizando desde Meta…
          </p>
        )}
        {errorLista && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="wa-plantilla-error-lista">
            {errorLista}
          </p>
        )}
        {sinPlantillas && (
          <p
            className="rounded-md bg-slate-50 px-3 py-6 text-center text-sm text-slate-600"
            data-testid="wa-plantilla-vacio"
          >
            {wabaId
              ? "Esta marca no tiene plantillas aprobadas. Un administrador las crea en Plantillas."
              : "Falta el WABA ID de esta marca en Canal."}
          </p>
        )}
        {(lista.length > 0 || q) && (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar plantilla…"
                className="pl-9"
                data-testid="wa-plantilla-buscar"
              />
            </div>
            {!visibles.length ? (
              <p className="py-4 text-center text-sm text-slate-500">Ninguna plantilla coincide con la búsqueda.</p>
            ) : (
              <ul className="max-h-[50dvh] space-y-1.5 overflow-y-auto pr-1">
                {visibles.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => elegir(p)}
                      data-testid={`wa-plantilla-opcion-${p.id}`}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:border-emerald-300 hover:bg-emerald-50/40"
                    >
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-[13px] font-medium text-slate-900">{p.nombre}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                          {idiomaNombre(p.idioma)}
                        </span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
                          {categoriaNombre(p.categoria)}
                        </span>
                      </div>
                      <p className="mt-1 line-clamp-2 text-[13px] text-slate-600">{p.texto_cuerpo}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    );
  }

  const campos = elegida.campos || [];
  return (
    <div className="space-y-4" data-testid="wa-elegir-plantilla-detalle">
      <button
        type="button"
        onClick={() => setElegida(null)}
        data-testid="wa-plantilla-volver"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Elegir otra plantilla
      </button>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <p className="font-mono text-sm font-medium text-slate-900">{elegida.nombre}</p>
            <p className="text-xs text-slate-500">
              {idiomaNombre(elegida.idioma)} · {categoriaNombre(elegida.categoria)}
            </p>
          </div>
          {formatoArchivo && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="wa-plantilla-archivo">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <IconoArchivo formato={formatoArchivo} /> Encabezado con {ROTULO_ARCHIVO[formatoArchivo]}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {archivo
                  ? `Se manda: ${archivo.nombre}`
                  : elegida.encabezado_archivo_id
                    ? "Se manda el archivo de ejemplo de la plantilla."
                    : `Esta plantilla no tiene un ${ROTULO_ARCHIVO[formatoArchivo]} guardado: elegí uno.`}
              </p>
              <input
                ref={pedirArchivo}
                type="file"
                accept={ACEPTA_ARCHIVO[formatoArchivo]}
                className="hidden"
                onChange={subir}
                data-testid="wa-plantilla-archivo-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 gap-1.5"
                disabled={subiendo}
                onClick={() => pedirArchivo.current?.click()}
                data-testid="wa-plantilla-cambiar-archivo"
              >
                {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                {archivo || elegida.encabezado_archivo_id ? "Cambiar archivo" : "Elegir archivo"}
              </Button>
            </div>
          )}
          {campos.map((c) => {
            const clave = `${c.grupo}:${c.clave}`;
            return (
              <div key={clave} className="space-y-1">
                <Label htmlFor={`wa-plantilla-campo-${c.grupo}-${c.clave}`} className="text-xs text-slate-600">
                  {c.etiqueta}
                  {c.grupo === "encabezado" ? " (encabezado)" : ""}
                </Label>
                <Input
                  id={`wa-plantilla-campo-${c.grupo}-${c.clave}`}
                  value={vals[clave] ?? ""}
                  onChange={(e) => setVals((x) => ({ ...x, [clave]: e.target.value }))}
                  placeholder={c.ejemplo || ""}
                  data-testid={`wa-plantilla-campo-${c.grupo}-${c.clave}`}
                />
              </div>
            );
          })}
          {!campos.length && !formatoArchivo && (
            <p className="text-sm text-slate-500">Esta plantilla no tiene variables para completar.</p>
          )}
        </div>
        <div className="rounded-lg bg-[#efeae2] p-3" data-testid="wa-plantilla-vista">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-500">Así lo va a ver el cliente</p>
          <div className="ml-auto max-w-[94%] rounded-lg rounded-tr-none bg-[#d9fdd3] p-1 pb-2 shadow-sm">
            {formatoArchivo && (
              <div className="mb-1 flex h-24 items-center justify-center gap-2 rounded bg-black/10 px-2 text-slate-600">
                <IconoArchivo formato={formatoArchivo} />
                <span className="truncate text-xs">{archivo?.nombre || ROTULO_ARCHIVO[formatoArchivo]}</span>
              </div>
            )}
            <BloquePlantilla partes={vista.partes} nombre={elegida.nombre} />
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500" data-testid="wa-plantilla-costo">
        {categoriaNombre(elegida.categoria)}. {NOTA_COSTO}
      </p>
      {faltan.length > 0 && <p className="text-xs text-amber-700">{faltan[0]}</p>}
      {errorEnvio && (
        <p
          role="alert"
          className="flex items-start gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="wa-plantilla-error"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorEnvio}</span>
        </p>
      )}
      <div className="flex justify-end">
        <Button
          type="button"
          onClick={enviar}
          disabled={!puede}
          className="gap-2 bg-emerald-600 hover:bg-emerald-700"
          data-testid="wa-plantilla-enviar"
        >
          {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {textoBoton || "Enviar plantilla"}
        </Button>
      </div>
    </div>
  );
}

// Mandarle una plantilla a una conversacion que ya existe (ventana de 24 h cerrada o no).
export function ElegirPlantillaDialog({ open, onOpenChange, conversacion, onCambio }) {
  const alEnviar = async (pedido) => {
    try {
      const { data } = await api.post(`/whatsapp/conversaciones/${conversacion.id}/plantilla`, pedido);
      toast.success("Plantilla enviada.");
      onOpenChange(false);
      if (onCambio) onCambio(data);
    } catch (e) {
      // si Meta la rechazo, el hilo muestra el mensaje fallido con la explicacion
      if (onCambio) onCambio(null);
      throw e;
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto" data-testid="wa-plantilla-dialogo">
        <DialogHeader>
          <DialogTitle>Mandar una plantilla</DialogTitle>
          <DialogDescription>
            Con una plantilla aprobada por Meta le podés escribir al cliente aunque hayan pasado más de 24 horas.
          </DialogDescription>
        </DialogHeader>
        {open && conversacion ? (
          <ElegirPlantilla
            marcaSlug={conversacion.marca_slug}
            idioma={conversacion.idioma}
            sugeridos={conversacion.valores_sugeridos}
            onEnviar={alEnviar}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
